/**
 * When to apply a service-worker update: the moment nothing on the page would
 * be lost by a reload, and not before (Q04).
 *
 * A new worker taking control ends in `location.reload()`, which destroys an
 * open inline draft, a Details draft or a popup form as surely as closing the
 * tab would -- with no question asked, because the registration used to be
 * `autoUpdate` and reloaded the instant the worker activated. Now the worker
 * waits, and this decides when to let it through.
 *
 * `clean()` is the app's own reading of whether anything is held: a typed
 * draft, a discard question still on screen. It is asked once now and, if it
 * says no, again after each of the events a draft can end with -- a submit, a
 * blur, a key, a click on Save or Discard, a dialog closing, a navigation, the
 * page going to the background. Event-driven rather than a store hook because
 * Escape, Cancel and Discard write nothing; generic rather than a call from
 * every close path because a close path this forgot would fail silently.
 *
 * The re-check waits a macrotask: these listen in the capture phase, before
 * the handler that resolves the draft has run, and a microtask would still see
 * the draft. `focusout` in particular fires on the mousedown that precedes the
 * click on Save, which is why `click` is in the list at all.
 *
 * `apply` runs once. A second call while one is armed folds into it, since the
 * update it would apply is the same update -- and prompts the first wait to
 * look again, so a page that has become clean since is not made to wait for
 * the next event or the clock. Checked before the clean fast path, or the
 * second call would apply at once and the first wait apply again after it.
 *
 * And the store's write chain (g67): a draft that ends with Enter or Save
 * leaves `clean()` true a few milliseconds before IndexedDB has the write,
 * and a reload in between loses the edit from the screen, the device and the
 * server. `applyWhenSettled` waits for both.
 *
 * And the document's own changes, because not every draft ends with an event
 * on it: a panel that closes after an awaited write removes the Save button
 * that had the focus, and a removed element fires no blur; a row whose editor
 * holds a draft can be deleted from another device. A draft's end nearly
 * always changes the tree, so a mutation observer re-checks on any change,
 * and a slow clock beside it makes even a draft that changed nothing finite.
 */

const DOCUMENT_EVENTS = ["focusout", "submit", "keydown", "click", "close", "visibilitychange"] as const;
const WINDOW_EVENTS = ["popstate"] as const;
/** How long a draft that ended without an event waits for its update, at most. */
export const RECHECK_MS = 15_000;

/** The armed wait's own re-check, while one is armed. */
let armed: (() => void) | undefined;

/**
 * Whether something is typed on a screen outside the frame -- the sign-in
 * form, before there is anyone to draw a frame for. Any text field holding a
 * value, not only the focused one: an address is as much a draft after Tab
 * has moved to the button as before. Inside the frame this is the wrong
 * reading (an opened editor is full of text nobody typed), and the frame and
 * the screen answer for themselves there.
 */
export function heldOutsideFrame(): boolean {
  return holdsText(document);
}

/**
 * Whether any text field under `root` holds a value. The right reading for
 * a surface whose fields start empty -- the bare screens, a popup's form --
 * where anything in a field was typed, and the wrong one for an editor
 * opened over existing text.
 */
export function holdsText(root: ParentNode): boolean {
  const fields = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    'input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]), textarea',
  );
  for (const field of fields) if (field.value !== "") return true;
  return false;
}

export function applyWhenClean(clean: () => boolean, apply: () => void): void {
  if (armed) {
    armed();
    return;
  }
  if (clean()) {
    apply();
    return;
  }

  let queued: ReturnType<typeof setTimeout> | undefined;
  let clock: ReturnType<typeof setInterval> | undefined;
  const observer = new MutationObserver(() => recheck());
  const recheck = (): void => {
    if (queued !== undefined) return;
    queued = setTimeout(() => {
      queued = undefined;
      if (!clean()) return;
      for (const name of DOCUMENT_EVENTS) document.removeEventListener(name, recheck, true);
      for (const name of WINDOW_EVENTS) window.removeEventListener(name, recheck, true);
      observer.disconnect();
      clearInterval(clock);
      armed = undefined;
      apply();
    }, 0);
  };
  armed = recheck;

  for (const name of DOCUMENT_EVENTS) document.addEventListener(name, recheck, true);
  for (const name of WINDOW_EVENTS) window.addEventListener(name, recheck, true);
  // Subtree changes and the two attributes a surface closes with; one
  // coalesced re-check per burst, so a repaint costs a handful of reads.
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden", "open"] });
  clock = setInterval(recheck, RECHECK_MS);
}

/** What the page's stores can say about their writes: `UpdateWiring`'s half of it. */
export interface Stored {
  /** Whether every write begun is in IndexedDB. Synchronous, so it is checked in the acting task. */
  stored(): boolean;
  /** Resolves once the writes have drained. */
  whenStored(): Promise<void>;
}

/**
 * `applyWhenClean`, and then only with every write stored (g67). Checked and
 * acted on in one task, with no `await` between: an edit begun after the
 * check would make `clean()` or `stored()` false, and the act would not run.
 * If writes are outstanding it waits for them and goes through both gates
 * again, since a draft can begin while it waits.
 */
export function applyWhenSettled(clean: () => boolean, writes: Stored, act: () => void): void {
  applyWhenClean(clean, () => {
    if (writes.stored()) {
      act();
      return;
    }
    void writes.whenStored().then(() => applyWhenSettled(clean, writes, act));
  });
}

/** How long a reload that may have been dropped is given before it is asked for again. */
export const RELOAD_AGAIN_MS = 1_000;

/**
 * A reload asked for while a history traversal is in flight -- the popup's
 * Discard and a panel's close both spend their entry with `history.back()`
 * -- can be the navigation Chromium drops. Asked again each second until the
 * document is gone; a timer does not survive the reload that works. Each
 * time through the gate, not only the first: the second a dropped reload
 * bought is time enough to begin a draft, and a retry that skipped the gate
 * would reload over it.
 */
export function reloadUntilGone(
  clean: () => boolean,
  writes: Stored,
  reload: () => void,
  later: (again: () => void) => void = (again) => {
    setTimeout(again, RELOAD_AGAIN_MS);
  },
): void {
  const again = (): void =>
    applyWhenSettled(clean, writes, () => {
      reload();
      later(again);
    });
  again();
}

/** What the page can do about an update: what `updateCallbacks` is wired to. */
export interface UpdateWiring extends Stored {
  /** Tells the waiting worker to take over. */
  skip(): void | Promise<void>;
  /** Whether a worker is still waiting to take over. */
  waiting(): Promise<boolean>;
  /** Reloads the document. */
  reload(): void;
}

/**
 * The two callbacks `registerSW` takes, composed from the pieces above so
 * that the composition is the tested thing and `main.ts` holds one spread.
 * A new worker is told to take over when nothing on the page would be lost
 * by the reload that follows -- no draft, and no write short of IndexedDB --
 * and told again while it waits; the reload its
 * taking control asks for goes through the same gate, and is asked for
 * again each second while a dropped one may have left the document standing.
 */
export function updateCallbacks(
  clean: () => boolean,
  wiring: UpdateWiring,
): { onNeedRefresh(): void; onNeedReload(): void } {
  return {
    onNeedRefresh: () =>
      applyWhenSettled(clean, wiring, () =>
        void insist(async () => {
          await wiring.skip();
        }, wiring.waiting),
      ),
    onNeedReload: () => reloadUntilGone(clean, wiring, wiring.reload),
  };
}

/** How often, and for how long at most, a waiting worker is told again. */
export const INSIST_MS = 2_000;
export const INSIST_FOR_MS = 30_000;

/**
 * Tells the waiting worker to take over, and keeps telling it while it still
 * waits, for a bounded while.
 *
 * Skip-waiting sets a flag on the worker and tries activation once. If the
 * worker that is active at that instant is still activating, or has an event
 * in flight, the try bails and nothing runs it again for us -- the update sits
 * waiting, and the reload that ends this never comes. Sending again re-runs
 * the try. `send` is the registration's own skip-waiting; `stillWaiting`
 * reads the registration. Bounded, because a worker that will not activate
 * in thirty seconds is a worker to stop asking.
 */
export async function insist(
  send: () => Promise<void>,
  stillWaiting: () => Promise<boolean>,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<void> {
  const started = Date.now();
  for (;;) {
    await send();
    await wait(INSIST_MS);
    if (!(await stillWaiting())) return;
    if (Date.now() - started >= INSIST_FOR_MS) return;
  }
}
